import { cartApi } from '../api/cartApi';
import { mapCartResponse } from '../data/cartData';
import { ErrorCode } from '../errors/catalog';
import type { Result } from '../errors/types';
import { normalizeError } from '../errors/normalizer';
import type { CartData, RawCartData } from '../types/cartTypes';
import { useCartStore } from '../store/useCartStore';

let inFlight: Promise<Result<CartData>> | null = null;
let scheduled: Promise<Result<CartData>> | null = null;
let timerId: number | null = null;

type RefreshOptions = {
  showLoading?: boolean;
  debounceMs?: number;
};

export async function fetchCartSnapshot(showLoading: boolean): Promise<Result<CartData>> {
  if (showLoading) {
    useCartStore.getState().setLoading(true);
  }
  useCartStore.getState().setCartSyncStatus('syncing');

  try {
    const raw: RawCartData = await cartApi.getCart();
    const mapped = mapCartResponse(raw);
    useCartStore.getState().setCart(mapped);
    useCartStore.getState().setCartFetchedAt(Date.now());
    useCartStore.getState().setCartSyncStatus('synced');
    return { ok: true, data: mapped };
  } catch (error) {
    const message = error instanceof Error ? undefined : '购物车同步失败';
    const serviceError = normalizeError(error, {
      code: ErrorCode.CART_SYNC_FAILED,
      message,
      context: {
        domain: 'cart',
        endpoint: '/cart.js',
        method: 'GET',
        operation: 'fetch'
      }
    });
    useCartStore.getState().setCartSyncStatus('degraded', serviceError);
    return { ok: false, error: serviceError };
  } finally {
    if (showLoading) {
      useCartStore.getState().setLoading(false);
    }
  }
}

export function requestCartRefresh(reason?: string, opts: RefreshOptions = {}): Promise<Result<CartData>> {
  console.log('[cart][refresh] request', { reason: reason ?? 'unknown' });
  if (import.meta.env.MODE !== 'production') {
    console.debug('[cart][refresh] request', {
      reason: reason ?? 'unknown',
      inFlight: Boolean(inFlight),
      scheduled: Boolean(scheduled)
    });
  }

  if (inFlight) return inFlight;
  if (scheduled) return scheduled;

  const debounceMs = opts.debounceMs ?? 250;
  const showLoading = opts.showLoading ?? false;

  scheduled = new Promise((resolve) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    const schedule = typeof window === 'undefined' ? setTimeout : window.setTimeout;
    timerId = schedule(async () => {
      timerId = null;
      scheduled = null;
      inFlight = fetchCartSnapshot(showLoading);
      if (import.meta.env.MODE !== 'production') {
        console.debug('[cart][refresh] start', { reason: reason ?? 'unknown' });
      }
      const result = await inFlight;
      if (import.meta.env.MODE !== 'production') {
        console.debug('[cart][refresh] done', { reason: reason ?? 'unknown', ok: result.ok });
      }
      if (reason === 'after-write') {
        console.log('[cart][refresh] after-write cart', result.ok ? result.data : result.error);
      }
      inFlight = null;
      resolve(result);
    }, debounceMs);
  });

  return scheduled;
}
